import React from 'react';

import { useIntl } from 'react-intl';
import { Select } from 'antd';
import type { Moment } from 'moment';

import Title from '@src/components/BoxContainer/components/Title';
import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import { DateUtil } from '@src/packages/utils/dates';
import { SCHEDULE_TYPES } from '@src/constants/commons';
import RelativeAbsolutePeriodFilter, {
  type RelativeAbsolutePeriodValue,
} from '@src/components/RelativeAbsolutePeriodFilter';
import IntlMessages from '@src/util/IntlMessages';

import ScheduleSelector from '../../../SchedulesDashboard/components/ScheduleSelector';
import { dateTypes } from '../../../SchedulesDashboard/constants';
import {
  buildCheckpointRoutesFilterSetValueProps,
  checkpointRoutesFiltersToPeriodValue,
  filterValuesToCheckpointRoutesFiltersUi,
  periodValueToCheckpointRoutesFilterProps,
  SCHEDULES_REPORT_MAX_SPAN,
  type CheckpointRoutesChartFilters,
} from '../../checkpointRoutesFilterConfig';
import type { ScheduleDashboardDateType } from '../../types';

const { Option } = Select;

interface CheckpointRoutesDashboardFiltersProps {
  handleFilters: (props: SetValueProps | SetValueProps[]) => void;
  filters: FilterValues;
  chartFilters: CheckpointRoutesChartFilters;
  divisionIds: string[];
  disabledDate: (date: Moment) => boolean;
  onSchedulesChange: (schedules: CheckpointRoutesChartFilters['schedules']) => void;
  onScheduleSelectorLoading: (loading: boolean) => void;
}

const CheckpointRoutesDashboardFilters: React.FC<CheckpointRoutesDashboardFiltersProps> = ({
  handleFilters,
  filters,
  chartFilters,
  divisionIds,
  onSchedulesChange,
  onScheduleSelectorLoading,
}) => {
  const intl = useIntl();
  const uiFilters = filterValuesToCheckpointRoutesFiltersUi(filters, chartFilters);
  const { groupBy, schedules } = uiFilters;
  const periodValue = checkpointRoutesFiltersToPeriodValue(uiFilters);

  const handlePeriodChange = (period: RelativeAbsolutePeriodValue) => {
    handleFilters(periodValueToCheckpointRoutesFilterProps(period, intl));
  };

  const handleGroupByChange = (value: ScheduleDashboardDateType) => {
    handleFilters(
      buildCheckpointRoutesFilterSetValueProps('checkpointRoutes.groupBy', value, intl),
    );
  };

  return (
    <div className="gx-flex-column gx-guarnic-gap-3">
      <RelativeAbsolutePeriodFilter
        historicalOnly
        value={periodValue}
        onChange={handlePeriodChange}
        label={<IntlMessages id="dashboards.schedules.dateRange" />}
        allowClear={false}
        defaultAbsoluteRange={() => [...chartFilters.dateRange]}
        maxSpan={SCHEDULES_REPORT_MAX_SPAN}
        showPresets
        timeZone={DateUtil.getDefaultTimeZone()}
      />
      <KeyValueWidget
        label={<Title.Filter value={<IntlMessages id="dashboards.schedules.groupResultsBy" />} />}
        value={
          <Select<ScheduleDashboardDateType>
            id="groupBy"
            onChange={handleGroupByChange}
            value={groupBy}
            dropdownMatchSelectWidth={false}
          >
            {dateTypes.map(d => (
              <Option key={d} value={d}>
                <IntlMessages id={`dateTypes.${d}`} values={{ amount: 1 }} />
              </Option>
            ))}
          </Select>
        }
      />
      <KeyValueWidget
        label={
          <Title.Filter
            value={<IntlMessages id="dashboards.chart.form.scheduleCompliance.groupBy.routes" />}
          />
        }
        value={
          <ScheduleSelector
            selectedSchedules={schedules}
            setSelected={onSchedulesChange}
            setLoading={onScheduleSelectorLoading}
            divisionIds={divisionIds}
            type={SCHEDULE_TYPES.checkpoint_route}
          />
        }
      />
    </div>
  );
};

export default CheckpointRoutesDashboardFilters;
